import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, FormControlLabel, Grid, IconButton, Modal, Switch, TextField, Typography } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import { useParams } from 'react-router-dom';
import { addDays } from 'date-fns';
import './utils/css/small.css';
import Centro from '../components/Centro';
import MainCalendar from '../components/Calendario/MainCalendar';
import { useServer } from '../context/ServerContext';
import EmailsPorServicio from './utils/EmailsPorServicio';
import { guardarEtiqueta, labelId } from '../features/labels/labels';
import { useLabels } from '../features/labels/LabelsProvider';
import { CrearCentro, EditarCentro, ObtenerCentros } from '../utils/apiServer';

const CreateCenters = ({ translate, editCentro, onEditConsumed }) => {
  const { refresh } = useLabels();
  const superuser = localStorage.getItem("cargo") === "Superusuario";
  const [visibleName, setVisibleName] = useState("");
  const [labelBaseline, setLabelBaseline] = useState("");
  const [persistedCenterId, setPersistedCenterId] = useState(null);
  const [saving, setSaving] = useState(false);
  const t = (key, fallback) => {
    const value = typeof translate === 'function' ? translate(key) : key;
    return value && value !== key ? value : fallback;
  };

  const { servidor } = useServer();
  const { token } = useParams();
  const emailsRef = useRef(null);
  const editBaselineRef = useRef(null);

  const [tipoUbicacion, setTipoUbicacion] = useState('centro');
  const [centros, setCentros] = useState([]);
  const [centroSeleccionado, setCentroSeleccionado] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [editar, setEditar] = useState(false);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [datesTouched, setDatesTouched] = useState(false);
  const [fieldsTouched, setFieldsTouched] = useState({ specie: false, gps: false });
  const [gpsMode, setGpsMode] = useState('manual');
  const [state, setState] = useState([{ startDate: addDays(new Date(), -7), endDate: new Date(), key: 'selection' }]);
  const [nuevoCentro, setNuevoCentro] = useState({
    enviro: { init: null, fin: null },
    nanoox: { init: null, fin: null },
    pss: { init: null, fin: null },
    scapp: { init: null, fin: null },
    flux: { init: null, fin: null },
  });
  const [centroInfo, setCentroInfo] = useState({ centro: '', area: '', region: '', specie: '', lat: '', lon: '' });
  const [emails, setEmails] = useState({ flux: [], enviro: [], nanoox: [], scapp: [] });

  useEffect(() => {
    if (!Array.isArray(state) || !state[0]?.startDate || !state[0]?.endDate) return;
    const formatDate = (value) => (value instanceof Date ? value : new Date(value)).toISOString().split('T')[0];
    const init = formatDate(state[0].startDate);
    const fin = formatDate(state[0].endDate);
    setNuevoCentro((prev) => ({
      ...prev,
      enviro: { init, fin },
      nanoox: { init, fin },
      pss: { init, fin },
      scapp: { init, fin },
      flux: { init, fin },
    }));
  }, [state]);

  useEffect(() => {
    if (editar) {
      setIsButtonDisabled(false);
      return;
    }
    setIsButtonDisabled(!(centroInfo.centro && centroInfo.area && centroInfo.region));
  }, [centroInfo, editar]);

  useEffect(() => {
    if (tipoUbicacion === 'barco') {
      setCentroInfo((prev) => ({ ...prev, region: 'flux', area: 'barcos' }));
    } else {
      setCentroInfo((prev) => ({ ...prev, region: '', area: '' }));
    }
  }, [tipoUbicacion]);

  const actualizarListaCentros = async () => {
    try {
      const usuariosObtenidos = await ObtenerCentros(servidor, token);
      setCentros(usuariosObtenidos);
    } catch (error) {
      console.error('Error al obtener centros:', error);
    }
  };

  useEffect(() => {
    actualizarListaCentros();
  }, []);

  const handleGpsToggle = () => {
    setGpsMode((prev) => (prev === 'manual' ? 'auto' : 'manual'));
    setFieldsTouched((prev) => ({ ...prev, gps: true }));
  };

  const handleChange = (event) => {
    let { name, value } = event.target;
    value = value.toLowerCase().trim();
    setCentroInfo((prev) => ({ ...prev, [name]: value }));
    if (name === 'specie') setFieldsTouched((prev) => ({ ...prev, specie: true }));
    if (name === 'lat' || name === 'lon') setFieldsTouched((prev) => ({ ...prev, gps: true }));
  };

  const resetForm = () => {
    setVisibleName(""); setLabelBaseline(""); setPersistedCenterId(null);
    setCentroInfo({ centro: '', area: '', region: '', specie: '', lat: '', lon: '' });
    setNuevoCentro({
      enviro: { init: null, fin: null },
      nanoox: { init: null, fin: null },
      pss: { init: null, fin: null },
      scapp: { init: null, fin: null },
      flux: { init: null, fin: null },
    });
    setState([{ startDate: addDays(new Date(), -7), endDate: new Date(), key: 'selection' }]);
  };

  const handleSubmit = async (event) => {
    try {
      event.preventDefault();
      const centro_id = `${centroInfo.centro}-${centroInfo.area}-${centroInfo.region}`;
      emailsRef.current?.commitPendingEmail();
      const gps_coordinates = {
        active: gpsMode,
        ...(gpsMode === 'manual' && {
          manual: { lat: parseFloat(centroInfo.lat), lon: parseFloat(centroInfo.lon) },
        }),
      };
      const newCentro = {
        centro_id,
        cicle_info: { specie: centroInfo.specie },
        gps_coordinates,
        ...nuevoCentro,
        emails,
      };
      setSaving(true);
      if (!persistedCenterId) {
        await CrearCentro([newCentro], setAlertMessage, setAlertSeverity, translate, servidor, token);
        setPersistedCenterId(centro_id);
        setCentros((prev) => [...prev, newCentro]);
      }
      if (superuser && visibleName.trim()) {
        try {
          await guardarEtiqueta(servidor, 'centro', persistedCenterId || centro_id, visibleName);
          await refresh();
        } catch (error) {
          setAlertMessage(t('centros_cycle_saved_label_failed', 'Ciclo guardado. Reintenta guardar el nombre visible.'));
          setAlertSeverity('error');
          return;
        }
      }
      setAlertMessage(t("centros_saved", "Cambios guardados."));
      setAlertSeverity("success");
      resetForm();
      await actualizarListaCentros();
    } catch (error) {
      console.error('Error al crear centro:', error);
    } finally { setSaving(false); }
  };

  const handleEdit = (centro) => {
    setEditar(true);
    setCentroSeleccionado(centro.centro_id);
    const name = labelId(centro.centro_id, 'car', '');
    setVisibleName(name); setLabelBaseline(name); setPersistedCenterId(null);
    const specie = centro?.cicle_info?.specie || '';
    const activeMode = centro?.gps_coordinates?.active === 'manual' ? 'manual' : 'auto';
    const manualLat = centro?.gps_coordinates?.manual?.lat ?? '';
    const manualLon = centro?.gps_coordinates?.manual?.lon ?? '';
    setCentroInfo((prev) => ({ ...prev, specie, lat: manualLat, lon: manualLon }));
    setGpsMode(activeMode);
    setNuevoCentro({
      enviro: { init: centro.enviro?.init, fin: centro.enviro?.fin },
      nanoox: { init: centro.nanoox?.init, fin: centro.nanoox?.fin },
      pss: { init: centro.pss?.init, fin: centro.pss?.fin },
      scapp: { init: centro.scapp?.init, fin: centro.scapp?.fin },
      flux: { init: centro.flux?.init, fin: centro.flux?.fin },
    });
    const window = ['enviro', 'nanoox', 'pss', 'scapp', 'flux'].map((s) => centro[s]).find((s) => s?.init && s?.fin);
    const calendarDate = (value) => {
      const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value);
      return Number.isNaN(date.getTime()) ? new Date() : date;
    };
    setState([{ startDate: window ? calendarDate(window.init) : new Date(), endDate: window ? calendarDate(window.fin) : new Date(), key: 'selection' }]);
    editBaselineRef.current = {
      specie,
      gps: { active: activeMode, manual: { lat: manualLat, lon: manualLon } },
      dates: {
        enviro: { ...centro.enviro },
        nanoox: { ...centro.nanoox },
        pss: { ...centro.pss },
        scapp: { ...centro.scapp },
        flux: { ...centro.flux },
      },
    };
    setDatesTouched(false);
    setFieldsTouched({ specie: false, gps: false });
  };

  useEffect(() => {
    if (editCentro) {
      handleEdit(editCentro);
      onEditConsumed();
    }
  }, [editCentro]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmEdit = async (event) => {
    try {
      event.preventDefault();
      const patch = {};
      const baseline = editBaselineRef.current || {};
      if (datesTouched) {
        patch.enviro = { ...nuevoCentro.enviro };
        patch.nanoox = { ...nuevoCentro.nanoox };
        patch.pss = { ...nuevoCentro.pss };
        patch.scapp = { ...nuevoCentro.scapp };
        patch.flux = { ...nuevoCentro.flux };
      }
      if (fieldsTouched.specie && centroInfo.specie !== baseline.specie) patch.cicle_info = { specie: centroInfo.specie };
      const nextGps = {
        active: gpsMode,
        ...(gpsMode === 'manual'
          ? { manual: { lat: parseFloat(centroInfo.lat), lon: parseFloat(centroInfo.lon) } }
          : {}),
      };
      const sameMode = nextGps.active === baseline?.gps?.active;
      const sameManual =
        (nextGps.active !== 'manual' && baseline?.gps?.active !== 'manual') ||
        (nextGps.active === 'manual' &&
          baseline?.gps?.active === 'manual' &&
          Number(nextGps.manual?.lat) === Number(baseline.gps?.manual?.lat) &&
          Number(nextGps.manual?.lon) === Number(baseline.gps?.manual?.lon));
      if (fieldsTouched.gps && !(sameMode && sameManual)) patch.gps_coordinates = nextGps;
      if (Object.keys(patch).length === 0 && visibleName === labelBaseline) {
        setAlertMessage(translate?.('no_hay_cambios') || 'No hay cambios para guardar.');
        setAlertSeverity('info');
        return;
      }
      setSaving(true);
      if (Object.keys(patch).length) {
        await EditarCentro(patch, setAlertMessage, setAlertSeverity, translate, centroSeleccionado, servidor, token);
        editBaselineRef.current = { specie: centroInfo.specie, gps: nextGps, dates: nuevoCentro };
        setDatesTouched(false); setFieldsTouched({ specie: false, gps: false });
      }
      if (superuser && visibleName !== labelBaseline) {
        await guardarEtiqueta(servidor, 'centro', centroSeleccionado, visibleName);
        await refresh();
      }
      setAlertMessage(t("centros_saved", "Cambios guardados."));
      setAlertSeverity("success");
      resetForm();
      setEditar(false);
      await actualizarListaCentros();
    } catch (error) {
      console.error('Error al actualizar centro:', error);
      setAlertMessage(t('centros_save_error', 'No se pudo guardar. Reintenta.'));
      setAlertSeverity('error');
    } finally { setSaving(false); }
  };

  const cancelEdit = () => {
    resetForm();
    setEditar(false);
    editBaselineRef.current = null;
    setDatesTouched(false);
    setFieldsTouched({ specie: false, gps: false });
  };

  const capitalizeCenter = (value) => {
    if (!value) return '';
    const trimmedStr = value.split('-')[0].trim();
    return trimmedStr.charAt(0).toUpperCase() + trimmedStr.slice(1);
  };

  const startPreview = state?.[0]?.startDate ? new Date(state[0].startDate) : null;
  const endPreview = state?.[0]?.endDate ? new Date(state[0].endDate) : null;
  const rangeStart = startPreview && endPreview && startPreview > endPreview ? endPreview : startPreview;
  const rangeEnd = startPreview && endPreview && startPreview > endPreview ? startPreview : endPreview;
  const rangeDays = rangeStart && rangeEnd ? Math.floor((rangeEnd - rangeStart) / 86400000) + 1 : 0;
  const rangeLabel =
    rangeStart && rangeEnd
      ? `${rangeStart.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })} - ${rangeEnd.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}`
      : t('no_range_selected', 'Sin rango seleccionado');

  return (
    <Box sx={{ height: '100%', minHeight: 0, overflowY: 'auto', pr: '0.2vw' }}>
      <Grid container justifyContent="center" sx={{ cursor: 'default', userSelect: 'none', minHeight: '90vh', gap: '1vw' }}>
        <Grid item xs={12}>
          <Box sx={heroCardSx}>
            <Typography sx={heroKickerSx}>{t('configuration_access', 'Configuration')}</Typography>
            <Typography sx={heroTitleSx}>
              {!editar ? t('create_center', 'Definir ciclo') : `${t('edit_ciclo', 'Editar ciclo')} ${capitalizeCenter(String(centroSeleccionado))}`}
            </Typography>
            <Typography sx={heroTextSx}>{t('center_management_description', 'Define ciclos, ventanas de servicio, coordenadas y notificaciones desde una vista mas clara y ordenada.')}</Typography>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box sx={twoColumnLayoutSx}>
            <Box sx={leftColumnStackSx}>
              <Box sx={rangeSummaryPanelSx}>
                <Box sx={rangeSummaryMainSx}>
                  <Typography sx={stepKickerSx}>{t('temporal_range', 'Rango temporal')}</Typography>
                  <Typography sx={rangeSummaryTitleSx}>{t('calendar', 'Calendario')}</Typography>
                  <Typography sx={rangeSummaryTextSx}>{rangeLabel}</Typography>
                </Box>
                <Box sx={rangeSummarySideSx}>
                  <Box sx={summaryDaysCardSx}>
                    <Typography sx={summaryLabelSx}>{t('days', 'Dias')}</Typography>
                    <Typography sx={summaryDaysValueSx}>{rangeDays}</Typography>
                  </Box>
                  <IconButton aria-label={t("centros_window", "Ventana del servicio")} onClick={() => setCalendarModalOpen(true)} sx={calendarOpenButtonSx}>
                    <CalendarMonthIcon />
                  </IconButton>
                </Box>
              </Box>

              <Box sx={sectionCardSx}>
                <Typography sx={sectionKickerSx}>{t('notification_channels', 'Notificaciones')}</Typography>
                <Typography sx={sectionTitleSx}>{t('notification_emails', 'Correos por servicio')}</Typography>
                <Typography sx={sectionTextSx}>{t('notification_emails_description', 'Administra los correos que recibiran alertas y eventos operativos por cada servicio.')}</Typography>
                <Box sx={{ mt: '0.7vw' }}>
                  <EmailsPorServicio ref={emailsRef} emails={emails} setEmails={setEmails} textfieldStyle={textfieldStyle} />
                </Box>
              </Box>
            </Box>

            <Box sx={stepSectionCardSx}>
              <Box sx={headerRowCompactSx}>
                <Box>
                  <Typography sx={sectionHeadingSx}>
                    {!editar ? t('location_data_title', 'Completa la informacion de la ubicacion') : t('edit_center_data_title', 'Ajusta los datos del ciclo seleccionado')}
                  </Typography>
                  {/* <Typography sx={sectionBodyCompactSx}>
                    {!editar
                      ? t('location_data_description', 'Primero define si crearas un centro o un barco. Luego completa la informacion base y la configuracion de coordenadas.')
                      : t('edit_center_data_description', 'Modifica especie, modo de coordenadas y ubicacion manual sin perder el contexto del ciclo activo.')}
                  </Typography> */}
                </Box>
                {/* <Box sx={{ display: 'flex', gap: '0.5vw', flexWrap: 'wrap' }}>
                  {!editar && <Box sx={pillSx(tipoUbicacion === 'centro')}>{tipoUbicacion === 'centro' ? t('center', 'Centro') : t('boat', 'Barco')}</Box>}
                  {editar && centroSeleccionado && <Box sx={pillSx(true)}>{capitalizeCenter(String(centroSeleccionado))}</Box>}
                </Box> */}
              </Box>

              <Box sx={configFormLayoutSx}>
                <Box sx={formBlockSx}>
                  <Typography sx={blockTitleSx}>{!editar ? t('location_identity', 'Identidad de la ubicacion') : t('cycle_summary', 'Resumen del ciclo')}</Typography>
                  <Typography sx={blockTextSx}>
                    {!editar ? t('location_identity_description', 'Usa estos campos para identificar la ubicacion de forma clara y evitar errores en la creacion.') : t('cycle_summary_description', 'Aqui puedes ajustar los datos operativos que cambian con mas frecuencia.')}
                  </Typography>

                  {!editar && (
                    <Box sx={toggleBoxSx}>
                      <Typography sx={toggleLegendSx}>{t('location_type', 'Tipo de ubicacion')}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.45vw' }}>
                        <Typography sx={toggleLabelSx(tipoUbicacion === 'centro')}>{t('center', 'Centro')}</Typography>
                        <Switch disabled={!!persistedCenterId || saving} checked={tipoUbicacion === 'barco'} onChange={() => setTipoUbicacion((prev) => (prev === 'centro' ? 'barco' : 'centro'))} sx={compactSwitchSx} />
                        <Typography sx={toggleLabelSx(tipoUbicacion === 'barco')}>{t('boat', 'Barco')}</Typography>
                      </Box>
                    </Box>
                  )}

                  {!editar && tipoUbicacion === 'barco' && (
                    <Box sx={noticeBoxSx}>
                      <Typography sx={noticeTitleSx}>{t('boat_defaults', 'Valores automaticos para barcos')}</Typography>
                      <Typography sx={noticeTextSx}>{t('boat_defaults_description', 'Al crear un barco se completa area = barcos y region = flux automaticamente.')}</Typography>
                    </Box>
                  )}

                  {editar && centroSeleccionado && (
                    <Box sx={noticeBoxSx}>
                      <Typography sx={noticeTitleSx}>{t('editing_center', 'Ubicacion seleccionada')}</Typography>
                      <Typography sx={noticeTextSx}>{labelId(centroSeleccionado, "car", capitalizeCenter(String(centroSeleccionado)))}</Typography>
                    </Box>
                  )}

                  <Box sx={compactFieldsGridSx}>
                    {superuser && <TextField label={t("centros_visible_name", "Nombre visible")} value={visibleName} onChange={(e) => setVisibleName(e.target.value)} inputProps={{ maxLength: 60 }} helperText={t("centros_name_help", "Vacío conserva el nombre actual.")} fullWidth sx={{ ...textfieldStyle, gridColumn: "1 / -1" }} disabled={saving} />}
                    {!editar && tipoUbicacion === 'centro' && (
                      <>
                        <TextField disabled={!!persistedCenterId} name="region" placeholder={translate('region')} value={centroInfo.region} onChange={handleChange} fullWidth label={translate('region')} InputLabelProps={{ shrink: true }} sx={textfieldStyle} />
                        <TextField disabled={!!persistedCenterId} name="area" placeholder={translate('area')} value={centroInfo.area} onChange={handleChange} fullWidth label={translate('area')} InputLabelProps={{ shrink: true }} sx={textfieldStyle} />
                      </>
                    )}
                    {!editar && (
                      <TextField disabled={!!persistedCenterId} placeholder={tipoUbicacion === 'centro' ? translate('centro') : 'Barco'} name="centro" value={centroInfo.centro} onChange={handleChange} fullWidth label={tipoUbicacion === 'centro' ? translate('centro') : 'Barco'} InputLabelProps={{ shrink: true }} sx={textfieldStyle} />
                    )}
                    <TextField name="specie" placeholder={translate('Especie')} value={centroInfo.specie} onChange={handleChange} fullWidth label={translate('Especie')} InputLabelProps={{ shrink: true }} sx={textfieldStyle} />
                  </Box>
                </Box>

                <Box sx={formBlockSx}>
                  <Typography sx={blockTitleSx}>{t('coordinates', 'Coordenadas')}</Typography>
                  <Typography sx={blockTextSx}>{t('coordinates_description', 'Elige si la posicion se gestionara automaticamente o si quieres ingresar latitud y longitud manualmente.')}</Typography>
                  <Box sx={toggleBoxSx}>
                    <Typography sx={toggleLegendSx}>{t('gps_mode', 'Modo GPS')}</Typography>
                    <FormControlLabel
                      control={<Switch checked={gpsMode === 'manual'} onChange={handleGpsToggle} sx={compactSwitchSx} />}
                      sx={formControlLabelSx}
                      label={gpsMode === 'manual' ? 'Manual' : 'Auto'}
                    />
                  </Box>
                  {gpsMode === 'manual' ? (
                    <Box sx={compactFieldsGridSx}>
                      <TextField name="lat" placeholder={translate('Latitud')} value={centroInfo.lat} onChange={handleChange} fullWidth label={translate('Latitud')} InputLabelProps={{ shrink: true }} sx={textfieldStyle} />
                      <TextField name="lon" placeholder={translate('Longitud')} value={centroInfo.lon} onChange={handleChange} fullWidth label={translate('Longitud')} InputLabelProps={{ shrink: true }} sx={textfieldStyle} />
                    </Box>
                  ) : (
                    <Box sx={noticeBoxSx}>
                      <Typography sx={noticeTitleSx}>{t('automatic_coordinates', 'Coordenadas automaticas activas')}</Typography>
                      <Typography sx={noticeTextSx}>{t('automatic_coordinates_description', 'No necesitas ingresar latitud ni longitud manualmente mientras el modo automatico este activo.')}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>

        <Modal open={calendarModalOpen} onClose={() => setCalendarModalOpen(false)}>
          <Box sx={calendarModalSx}>
            <Box sx={calendarModalHeaderSx}>
              <Box>
                <Typography sx={stepKickerSx}>{t('temporal_range', 'Rango temporal')}</Typography>
                <Typography sx={sectionHeadingSx}>{t('calendar', 'Calendario')}</Typography>
              </Box>
              <IconButton aria-label={t("cerrar", "Cerrar")} onClick={() => setCalendarModalOpen(false)} sx={calendarModalCloseSx}>
                <CloseIcon />
              </IconButton>
            </Box>

            <MainCalendar
              state={state}
              translate={translate}
              variant="config"
              hideToolbar
              setState={(next) => {
                setDatesTouched(true);
                setState(next);
              }}
            />
          </Box>
        </Modal>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button disabled={isButtonDisabled || saving} onClick={!editar ? handleSubmit : handleConfirmEdit} variant="contained" sx={primaryActionSx}>
              {editar ? translate('editar_centro') : translate('crear_centro')}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          {/* <Box sx={sectionCardSx}> */}
            {/* <Typography sx={sectionKickerSx}>{t('registered_locations', 'Registro actual')}</Typography>
            <Typography sx={sectionTitleSx}>{t('created_centers', 'Centros y barcos creados')}</Typography>
            <Typography sx={sectionTextSx}>{t('created_centers_description', 'Revisa la configuracion existente, selecciona un elemento y vuelve a editarlo cuando sea necesario.')}</Typography>
            <Box sx={{ mt: '1vw' }}> */}
              <Centro servidor={servidor} token={token} editar={editar} cancelEdit={cancelEdit} translate={translate} centros={centros} actualizarListaCentros={actualizarListaCentros} centroSeleccionado={centroSeleccionado} setCentroSeleccionado={setCentroSeleccionado} handleEdit={handleEdit} />
            {/* </Box> */}
          {/* </Box> */}
        </Grid>

        {alertMessage && (
          <Alert severity={alertSeverity} onClose={() => setAlertMessage(null)} style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999, fontSize: '2vh' }}>
            {alertMessage}
          </Alert>
        )}
      </Grid>
    </Box>
  );
};

export default CreateCenters;

const textfieldStyle = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    minHeight: 40,
    borderRadius: '0.85vw',
    // backgroundColor: 'rgba(255,255,255,0.03)',
    '& fieldset': { borderColor: 'rgba(233, 78, 26, .24)' },
    '&:hover fieldset': { borderColor: 'rgba(233, 78, 26, .55)' },
    '&.Mui-focused fieldset': { borderColor: 'rgba(233, 78, 26, .85)' },
    '& .MuiInputBase-input': { color: '#d8e0ea', fontSize: 'max(12px, 0.68vw)', padding: '10px' },
  },
  '& .MuiInputLabel-root': { color: '#aeb9c6', fontSize: 'max(12px, 0.64vw)' },
  '& .MuiInputLabel-shrink': { fontSize: 'max(12px, 0.58vw)' },
};

const heroCardSx = {
  border: '0.01vw solid rgba(233, 78, 26, 0.24)',
  borderRadius: '1vw',
  px: 'min(1.2vw, 2.4vh)',
  py: 'min(1vw,2vh)',
  background: 'radial-gradient(circle at top right, rgba(233, 78, 26, 0.12), transparent 28%), linear-gradient(180deg, rgba(15, 23, 38, 0.96), rgba(8, 14, 24, 0.96))',
  boxShadow: 'inset 0 0 0 0.01vw rgba(255,255,255,0.03)',
};

const sectionCardSx = {
  p: 'min(1.15vw,2.3vh)',
  border: '0.01vw solid rgba(233, 78, 26, 0.24)',
  borderRadius: '1vw',
  background: 'radial-gradient(circle at top right, rgba(233, 78, 26, 0.08), transparent 26%), linear-gradient(180deg, rgba(15, 23, 38, 0.96), rgba(8, 14, 24, 0.96))',
  boxShadow: 'inset 0 0 0 0.01vw rgba(255,255,255,0.03)',
  boxSizing: 'border-box',
};
const stepSectionCardSx = {
  ...sectionCardSx,
  p: '0.9vw',
  minHeight: 'min(23vw,46vh)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const heroKickerSx = { fontSize: 'min(0.56vw, 1.2vh)', color: '#e94e1a', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' };
const heroTitleSx = { mt: '0.25vh', fontSize: '1.45vw', fontWeight: 700, color: '#f3f5f8' };
const heroTextSx = { mt: '0.55vh', fontSize: '0.72vw', color: '#97a6b7', lineHeight: 1.6 };
const sectionKickerSx = { fontSize: '0.54vw', color: '#e94e1a', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', textAlign: 'center' };
const sectionTitleSx = { mt: '0.2vh', fontSize: '1.05vw', fontWeight: 700, color: '#f3f5f8', textAlign: 'center' };
const sectionTextSx = { mt: '0.4vh', fontSize: '0.7vw', color: '#97a6b7', textAlign: 'center', lineHeight: 1.6 };
const headerRowSx = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1vw', flexWrap: 'wrap' };
const stepKickerSx = { fontSize: '0.48vw', color: '#e94e1a', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' };
const sectionHeadingSx = { mt: '0.12vh', fontSize: '1.05vw', fontWeight: 700, color: '#f4f7fb', lineHeight: 1.15 };
// const sectionBodySx = { mt: '0.3vh', fontSize: '0.64vw', color: '#98a8ba', lineHeight: 1.45, maxWidth: '44vw' };
// const sectionBodyCompactSx = { ...sectionBodySx, maxWidth: '100%' };
// const summaryCardSx = { minWidth: '12vw', maxWidth: '16vw', borderRadius: '1vw', border: '0.01vw solid rgba(233, 78, 26, 0.2)', background: 'rgba(255,255,255,0.03)', px: '0.9vw', py: '0.9vh' };
// const summaryCardCompactSx = { ...summaryCardSx, minWidth: '10.2vw', maxWidth: '12.8vw', px: '0.75vw', py: '0.65vh' };
const summaryLabelSx = { fontSize: '0.48vw', color: '#8ea1b5', textTransform: 'uppercase', letterSpacing: '0.08em' };
// const summaryValueSx = { mt: '0.2vh', fontSize: '0.66vw', fontWeight: 700, color: '#f5f7fb', lineHeight: 1.3 };
// const summaryMetaSx = { mt: '0.18vh', fontSize: '0.58vw', color: '#ffb28e', fontWeight: 700 };
// const pillSx = (active) => ({ borderRadius: '999px', border: `0.01vw solid ${active ? 'rgba(233,78,26,0.5)' : 'rgba(255,255,255,0.12)'}`, background: active ? 'rgba(233,78,26,0.14)' : 'rgba(255,255,255,0.04)', color: active ? '#fff0e8' : '#9eb0c3', px: '0.85vw', py: '0.55vh', fontSize: '0.68vw', fontWeight: 700 });
const twoColumnLayoutSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: '1vw', alignItems: 'start' };
const leftColumnStackSx = { display: 'grid', gap: '1vw', minWidth: 0 };
const rangeSummaryPanelSx = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: '0.8vw',
  alignItems: 'stretch',
};
const rangeSummaryMainSx = {
  border: '0.01vw solid rgba(233, 78, 26, 0.24)',
  borderRadius: '1vw',
  px: '1vw',
  py: '0.95vw',
  background: 'radial-gradient(circle at top right, rgba(233, 78, 26, 0.12), transparent 30%), linear-gradient(180deg, rgba(15, 23, 38, 0.96), rgba(8, 14, 24, 0.96))',
  minWidth: 0,
};
const rangeSummaryTitleSx = { mt: '0.22vh', fontSize: '1.05vw', fontWeight: 700, color: '#f4f7fb', lineHeight: 1.1 };
const rangeSummaryTextSx = { mt: '0.55vh', fontSize: '0.74vw', color: '#b8c5d4', lineHeight: 1.35 };
const rangeSummarySideSx = { display: 'grid', gridTemplateRows: '1fr auto', gap: '0.6vw', minWidth: '5.6vw' };
const summaryDaysCardSx = {
  border: '0.01vw solid rgba(233, 78, 26, 0.24)',
  borderRadius: '1vw',
  px: '0.85vw',
  py: '0.8vw',
  background: 'linear-gradient(180deg, rgba(15, 23, 38, 0.96), rgba(8, 14, 24, 0.96))',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
};
const summaryDaysValueSx = { mt: '0.12vh', fontSize: '1.5vw', fontWeight: 800, color: '#ffffff', lineHeight: 1 };
const calendarOpenButtonSx = {
  borderRadius: '0.9vw',
  border: '0.01vw solid rgba(233, 78, 26, 0.24)',
  background: 'linear-gradient(180deg, rgba(15, 23, 38, 0.96), rgba(8, 14, 24, 0.96))',
  // background: 'rgba(255,255,255,0.03)',
  // color: '#e84e0e',
  width: '100%',
  minHeight: '3.4vh',
  '&:hover': {
    background: 'rgba(233, 78, 26, 0.12)',
    borderColor: 'rgba(233, 78, 26, 0.5)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.05vw',
  },
};
const headerRowCompactSx = { ...headerRowSx, gap: '0.8vw' };
// const calendarPanelSx = {
//   mt: '0.7vw',
//   borderRadius: '0.95vw',
//   border: '0.01vw solid rgba(233,78,26,0.18)',
//   background: 'rgba(0,0,0,0.12)',
//   overflow: 'hidden',
//   p: '0.7vw',
//   flex: 1,
//   minHeight: 0,
// };
// const formLayoutSx = { mt: '1vw', display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: '1vw' };
const configFormLayoutSx = { mt: '0.75vw', display: 'grid', gridTemplateColumns: '1fr', gap: '0.65vw', flex: 1, minHeight: 0 };
const formBlockSx = { borderRadius: '0.9vw', border: '0.01vw solid rgba(233,78,26,0.18)',  p: '0.75vw', minWidth: 0 };
const blockTitleSx = { fontSize: '0.82vw', fontWeight: 700, color: '#f4f7fb', lineHeight: 1.15 };
const blockTextSx = { mt: '0.2vh', fontSize: '0.6vw', color: '#95a7bb', lineHeight: 1.4 };
const toggleBoxSx = { mt: '0.55vw', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.6vw', flexWrap: 'wrap', borderRadius: '0.8vw', border: '0.01vw solid rgba(233,78,26,0.12)', background: 'rgba(7, 14, 25, 0.5)', px: '0.7vw', py: '0.5vh' };
const toggleLegendSx = { fontSize: '0.54vw', color: '#8ea1b5', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 };
const toggleLabelSx = (active) => ({ fontSize: '0.64vw', color: active ? '#e94e1a' : '#b6c2cf', fontWeight: active ? 700 : 500 });
// const fieldsGridSx = { mt: '1vw', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: '0.9vw' };
const compactFieldsGridSx = {
  mt: '0.65vw',
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
  gap: '0.65vw',
};
const formControlLabelSx = { m: 0, '& .MuiFormControlLabel-label': { color: '#d8e0ea', fontSize: '0.64vw', fontWeight: 600 } };
const noticeBoxSx = { mt: '0.65vw', borderRadius: '0.8vw', border: '0.01vw dashed rgba(233,78,26,0.22)', background: 'rgba(255,255,255,0.025)', px: '0.75vw', py: '0.65vh' };
const noticeTitleSx = { fontSize: '0.64vw', fontWeight: 700, color: '#f2f5f8' };
const noticeTextSx = { mt: '0.18vh', fontSize: '0.58vw', color: '#97a6b7', lineHeight: 1.35 };
const compactSwitchSx = {
  width: '1.8vw',
  height: '0.9vw',
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: '0.15vw',
    '&.Mui-checked': {
      transform: 'translateX(0.9vw)',
      color: '#fff',
      '& + .MuiSwitch-track': { backgroundColor: '#e94e1a', opacity: 1 },
    },
  },
  '& .MuiSwitch-thumb': { width: '0.6vw', height: '0.6vw' },
  '& .MuiSwitch-track': { borderRadius: '1vw', backgroundColor: 'rgba(200,200,200,0.3)', opacity: 1 },
};
const primaryActionSx = {
  fontSize: '0.82vw',
  fontWeight: 700,
  minWidth: '10vw',
  minHeight: '4.2vh',
  borderRadius: '0.9vw',
  textTransform: 'none',
  color: '#fff',
  background: 'linear-gradient(135deg, #ff6a2f, #e84e0e)',
  boxShadow: '0 0.4vw 0.9vw rgba(232, 78, 14, 0.24)',
  '&:hover': { background: 'linear-gradient(135deg, #ff7d4c, #f05f26)' },
};
const calendarModalSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'min(50vw, 100vh)',
  maxHeight: '82vh',
  overflow: 'auto',
  borderRadius: '1.1vw',
  border: '0.01vw solid rgba(233, 78, 26, 0.28)',
  background: 'radial-gradient(circle at top right, rgba(233, 78, 26, 0.12), transparent 24%), linear-gradient(180deg, rgba(12, 21, 34, 0.98), rgba(7, 14, 25, 0.98))',
  boxShadow: '0 1vw 2.6vw rgba(0,0,0,0.34)',
  p: '1vw',
  boxSizing: 'border-box',
};
const calendarModalHeaderSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '1vw',
  mb: '0.8vw',
};
const calendarModalCloseSx = {
  border: '0.01vw solid rgba(233, 78, 26, 0.24)',
  borderRadius: '0.9vw',
  color: '#dbe3ed',
  background: 'rgba(255,255,255,0.03)',
};
